import { getListAuditLogsQueryOptions, useListAuditLogsSuspense } from '@amarantos/api-client'
import { createFileRoute } from '@tanstack/react-router'
import { useDeferredValue } from 'react'
import { useTranslation } from 'react-i18next'
import { PageContent, PageHeader, PageTitle } from '@/components/page'
import { PaginationBar } from '@/components/pagination-bar'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Card, CardContent } from '@/components/ui/card'
import { AuditLogsFilters } from '@/features/audit-logs/components/audit-logs-filters'
import { AuditLogsTable } from '@/features/audit-logs/components/audit-logs-table'
import { auditLogsSearchSchema, toListAuditLogsParams } from '@/features/audit-logs/utils/search'
import { auditLogRequiredRoles, requirePanelRole } from '@/utils/roles'

export const Route = createFileRoute('/_authenticated/audit-logs')({
	validateSearch: auditLogsSearchSchema,
	// First role-gate in the app (ADR-0002). Navigation/UX only — the backend `403`
	// is what enforces access; a typed-in URL still can't bypass this redirect.
	beforeLoad: ({ context: { queryClient } }) => requirePanelRole(queryClient, auditLogRequiredRoles),
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, deps }) => {
		queryClient.ensureQueryData(getListAuditLogsQueryOptions(toListAuditLogsParams(deps)))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_audit_logs' />
	),
	component: AuditLogsRoute,
})

function AuditLogsRoute() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()
	const deferredSearch = useDeferredValue(search)
	const isStale = deferredSearch !== search

	const auditLogsQuery = useListAuditLogsSuspense(toListAuditLogsParams(deferredSearch))

	const logs = auditLogsQuery.data?.data ?? []
	const meta = auditLogsQuery.data?.meta
	const currentPage = search.page
	const totalPages = meta?.total_pages ?? 1

	return (
		<>
			<PageHeader breadcrumbs={[{ label: t('nav.audit_logs') }]}>
				<PageTitle subtitle={t('audit_log.page.description')}>{t('nav.audit_logs')}</PageTitle>
			</PageHeader>
			<PageContent>
				<Card>
					<CardContent className='space-y-4'>
						<AuditLogsFilters
							onApply={() => {
								navigate({ search: (previous) => ({ ...previous, page: 1 }) })
							}}
						/>

						<AuditLogsTable logs={logs} isStale={isStale} />

						<PaginationBar
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={(page) => navigate({ search: (previous) => ({ ...previous, page }) })}
						/>
					</CardContent>
				</Card>
			</PageContent>
		</>
	)
}
